// Orchestrates on-demand concert queries: turns filter-signal changes into
// /api/city-counts + /api/historical-city-counts (map markers) and a UNIFIED
// reading list merged chronologically from /api/concerts + /api/historical-concerts.
//
// The unified list is the load-bearing idea: there is no "live vs archive" mode any
// more, just one time axis. When today falls inside the window the list splits into
// two directions around it — "Upcoming" (today onward, soonest first) and "Past"
// (before today, most recent first) — because no reader wants 1842 at the top of a
// full-span window. Each direction is a k-way merge of the two server streams, both
// already sorted by the server in the direction being read.
import { effect } from "@preact/signals";
import {
  selectedComposerId,
  selectedPieceKey,
  selectedPiece,
  pieceWorkId,
  selectedOrgId,
  selectedCityId,
  radiusKm,
  radiusCenter,
  selectedCountry,
  selectedGenre,
  timeFrom,
  timeTo,
  anchorISO,
  listDirection,
  listDirectionExplicit,
  hintHistoryReplace,
  mapBounds,
  viewMode,
  liveEnabled,
  archiveEnabled,
  excludedArchiveKeys,
  certParam,
  showExtras,
  mentionCount,
  cityCounts,
  cityCountsLoaded,
  historicalCityCounts,
  historicalCityCountsLoaded,
  listItems,
  listLoading,
  listHasMore,
  listError,
  listEpoch,
  forwardTotal,
  backwardTotal,
  notPerformedTotal,
  totalsLoaded,
  listRefetching,
} from "../state/store";
import {
  addCityCounts,
  fetchConcerts,
  fetchExtraConcerts,
  fetchCityCounts,
  fetchExtraCityCounts,
  fetchPieceByKey,
  fetchHistoricalConcerts,
  fetchHistoricalCityCounts,
  fetchHistoricalMentions,
  type ConcertFilters,
  type CountFilters,
  type HistoricalConcertFilters,
} from "./api";
import type { ListItem } from "../types";

/** Per-source page size. Small enough that a filter change wastes little, large enough
 *  that the merge rarely needs a refill before the reader scrolls. */
const SOURCE_PAGE = 50;
/** Items appended to the visible list per emission step (initial fill and each
 *  infinite-scroll trigger). */
const EMIT_CHUNK = 30;

// ── Shared filter builders ─────────────────────────────────────────

/** Snap outward to a coarse grid. Exact viewport bounds make every device and every
 *  pan-pixel a distinct /api URL — no edge-cache reuse and a fresh query per jitter.
 *  Rounding OUTWARD (never inward) can only widen the filter, so nothing at the
 *  viewport edge is dropped; half a degree of slack is invisible at any zoom where a
 *  bbox is published at all. */
const BBOX_GRID = 0.5;
function quantizeBbox(b: [number, number, number, number]): [number, number, number, number] {
  const [west, south, east, north] = b;
  return [
    Math.floor(west / BBOX_GRID) * BBOX_GRID,
    Math.max(-90, Math.floor(south / BBOX_GRID) * BBOX_GRID),
    Math.ceil(east / BBOX_GRID) * BBOX_GRID,
    Math.min(90, Math.ceil(north / BBOX_GRID) * BBOX_GRID),
  ];
}

function bboxNow(): [number, number, number, number] | null {
  // The map only exists on the browse page; explore must not filter by stale bounds.
  const b = viewMode.value === "browse" ? mapBounds.value : null;
  return b ? quantizeBbox(b) : null;
}

/** The day before `iso`, as an ISO date. */
function dayBefore(iso: string): string {
  const d = new Date(`${iso}T12:00`);
  d.setDate(d.getDate() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export interface SegmentConfig {
  /** Live /api/concerts filters for this direction's segment, or null to skip live. */
  live: ConcertFilters | null;
  /** Historical filters, or null to skip the archive. */
  hist: HistoricalConcertFilters | null;
  /** /api/extra-concerts filters: the live filters, when the reader has opted in, else null. */
  extra: ConcertFilters | null;
}

/** Build the two per-source queries for one direction of the current window.
 *
 *  Anchor semantics: `forward` = [today, windowEnd] ascending; `backward` =
 *  [windowStart, yesterday] descending. Without an anchor (window excludes today)
 *  everything is one ascending segment. Boundary details:
 *   - live date_start can carry a time ("2026-07-19T19:30"), so the backward bound is
 *     yesterday + "T23:59:59" — a plain date would string-compare BELOW yesterday's
 *     timed concerts and drop them.
 *   - historical from/to is an overlap test server-side, so a multi-year event partly
 *     inside the segment is still included.
 *
 *  Sources that can't answer the active filters are excluded rather than guessed at:
 *  an org/genre filter has no historical join, so those filters silently turning
 *  the archive off IS the honest behavior (the list shows a note saying so). The piece
 *  filter joins historical_concert_pieces since 2026-08-13, so the archive stays on. */
function segmentConfig(
  direction: "forward" | "backward",
  opts: { wholeWindow?: boolean } = {},
): SegmentConfig {
  // `wholeWindow` ignores today's split: one ascending segment over the entire time window
  // (the CSV download's "both" choice).
  const anchor = opts.wholeWindow ? null : anchorISO.value;
  const windowFrom = timeFrom.value;
  const windowTo = timeTo.value || null;
  const bbox = bboxNow();

  // Radius search: a manual city widened to "within N km" queries by distance from the
  // city's coordinates instead of city_id equality. Until the static city list resolves
  // the center (cold load of a ?city=&km= link), fall back to the exact city so the list
  // is never unfiltered; the query re-runs when radiusCenter arrives.
  const cityId = selectedCityId.value;
  const center = radiusCenter.value;
  const km = radiusKm.value;
  const useRadius = cityId !== null && km !== null && center !== null;

  const common = {
    composer: selectedComposerId.value,
    city: useRadius ? null : cityId,
    near: useRadius ? ([center.lat, center.lon] as [number, number]) : null,
    km: useRadius ? km : null,
    country: selectedCountry.value || null,
    bbox,
  };

  let from: string;
  let liveTo: string | null;
  let histTo: string | null;
  let order: "asc" | "desc";
  if (anchor === null || direction === "forward") {
    from = anchor ?? windowFrom;
    liveTo = windowTo;
    histTo = windowTo;
    order = "asc";
  } else {
    from = windowFrom;
    const yesterday = dayBefore(anchor);
    liveTo = `${yesterday}T23:59:59`;
    histTo = yesterday;
    order = "desc";
  }

  const live: ConcertFilters | null = liveEnabled.value
    ? {
        ...common,
        piece: selectedPieceKey.value,
        genre: selectedGenre.value || null,
        org: selectedOrgId.value,
        from,
        to: liveTo,
        order,
      }
    : null;

  const histApplicable =
    archiveEnabled.value &&
    selectedOrgId.value === null &&
    !selectedGenre.value; // the archive layer has no genre concept
  const hist: HistoricalConcertFilters | null = histApplicable
    ? {
        ...common,
        piece: selectedPieceKey.value,
        xsrc: excludedArchiveKeys.value,
        cert: certParam.value,
        from,
        to: histTo,
        order,
      }
    : null;

  // Browse only: Explore never shows the list's rows, and its counts stay classical concerts.
  return { live, hist, extra: showExtras.value && viewMode.value === "browse" ? live : null };
}

/** The exact filters behind the list the reader is looking at — the CSV download exports
 *  THIS, so a spreadsheet always matches the screen (filters, time window and map view).
 *  `both` = the whole time window, ignoring the Upcoming/Past split. */
export function listFiltersFor(scope: "upcoming" | "past" | "both"): SegmentConfig {
  if (scope === "both") return segmentConfig("forward", { wholeWindow: true });
  return segmentConfig(scope === "upcoming" ? "forward" : "backward");
}

// ── The merge ──────────────────────────────────────────────────────

/** Composite sort key mirroring the server's ORDER BY exactly (see
 *  web/functions/api/concerts.ts): day, then display quality DESC within the day, then
 *  time (with the date-only → T24:00 ASC remap as tiebreak). Encoded as one string —
 *  day (10 chars) + "~" + 2-digit quality byte + time — so the k-way merge can keep
 *  using a single string compare. The quality byte is inverted under ASC (99−q) so
 *  higher quality sorts FIRST in both directions. Historical rows carry no quality
 *  score; they get a fixed midpoint (50) so they interleave neutrally within a day.
 *  NULL/empty live dates return "" (first asc / last desc — same as SQLite NULLs; the
 *  cross-source merge relies on that placement). */
const HIST_QUALITY = 50;

function sortKey(item: ListItem, order: "asc" | "desc"): string {
  let day: string;
  let time: string;
  let quality: number;
  if (item.kind === "live") {
    const ds = item.concert.date_start ?? "";
    if (!ds) return "";
    day = ds.slice(0, 10);
    const dateOnly = ds.length === 10 || ds.slice(11) === "00:00";
    time = order === "asc" && dateOnly ? "T24:00" : ds.slice(10);
    quality = item.concert.quality ?? 0;
  } else {
    const de = item.concert.date_earliest;
    day = de.slice(0, 10);
    time = de.slice(10);
    quality = HIST_QUALITY;
  }
  const qualityByte = order === "asc" ? 99 - quality : quality;
  return day + "~" + String(qualityByte).padStart(2, "0") + time;
}

interface Cursor {
  buffer: ListItem[];
  nextOffset: number;
  /** Every row the query matches — drives paging and the empty-side switch. */
  total: number;
  /** Rows of `total` that did not take place as listed (cancelled/postponed): they page like any
   *  other row but are left out of the displayed counts, like the map's (ISSUES #069). */
  notPerformed: number;
  hasMore: boolean;
  fetchPage: (offset: number, signal: AbortSignal) => Promise<{ items: ListItem[]; total: number; notPerformed: number; hasMore: boolean }>;
}

interface ActiveQuery {
  token: number;
  order: "asc" | "desc";
  cursors: Cursor[];
  controller: AbortController;
}

let current: ActiveQuery | null = null;
let listToken = 0;
/** Serialized inputs of the last query that ran to completion. A re-triggered effect
 *  whose inputs resolve to the same queries (bbox quantized to the same cell, a signal
 *  write that didn't change the outcome) is skipped instead of aborting and refetching
 *  an identical result set. Cleared on error so a retry is never suppressed. */
let lastCompletedKey: string | null = null;

function makeLiveCursor(f: ConcertFilters, fetchPage: typeof fetchConcerts = fetchConcerts): Cursor {
  return {
    buffer: [],
    nextOffset: 0,
    total: 0,
    notPerformed: 0,
    hasMore: true,
    fetchPage: async (offset, signal) => {
      const page = await fetchPage(f, { limit: SOURCE_PAGE, offset, signal });
      return {
        items: page.concerts.map((concert): ListItem => ({ kind: "live", concert })),
        total: page.total,
        notPerformed: page.notPerformed,
        hasMore: page.hasMore,
      };
    },
  };
}

function makeHistCursor(f: HistoricalConcertFilters): Cursor {
  return {
    buffer: [],
    nextOffset: 0,
    total: 0,
    notPerformed: 0,
    hasMore: true,
    fetchPage: async (offset, signal) => {
      const page = await fetchHistoricalConcerts(f, { limit: SOURCE_PAGE, offset, signal });
      return {
        items: page.concerts.map((concert): ListItem => ({ kind: "hist", concert })),
        total: page.total,
        notPerformed: 0,
        hasMore: page.hasMore,
      };
    },
  };
}

async function fillCursor(q: ActiveQuery, cursor: Cursor): Promise<void> {
  const page = await cursor.fetchPage(cursor.nextOffset, q.controller.signal);
  if (q.token !== listToken) return;
  cursor.buffer.push(...page.items);
  cursor.nextOffset += page.items.length;
  cursor.total = page.total;
  cursor.notPerformed = page.notPerformed;
  cursor.hasMore = page.hasMore;
}

/** Emit up to `n` more items in merged order, refilling source buffers as needed. The
 *  head of a cursor may only be emitted once every other cursor either has a comparable
 *  head or is definitively exhausted — otherwise an unfetched page could contain an
 *  earlier item. */
async function emitMerged(q: ActiveQuery, n: number): Promise<ListItem[]> {
  const out: ListItem[] = [];
  while (out.length < n) {
    // Refill any empty-but-unfinished cursor before comparing heads.
    for (const cursor of q.cursors) {
      if (cursor.buffer.length === 0 && cursor.hasMore) {
        await fillCursor(q, cursor);
        if (q.token !== listToken) return out;
      }
    }
    const candidates = q.cursors.filter((c) => c.buffer.length > 0);
    if (candidates.length === 0) break;
    let best = candidates[0];
    for (const c of candidates.slice(1)) {
      const cmp = sortKey(c.buffer[0], q.order).localeCompare(sortKey(best.buffer[0], q.order));
      if (q.order === "asc" ? cmp < 0 : cmp > 0) best = c;
    }
    out.push(best.buffer.shift()!);
  }
  return out;
}

function queryHasMore(q: ActiveQuery): boolean {
  return q.cursors.some((c) => c.buffer.length > 0 || c.hasMore);
}

// ── Query lifecycle ────────────────────────────────────────────────

async function runUnifiedQuery(): Promise<void> {
  const anchor = anchorISO.value;
  const direction = anchor === null ? "forward" : listDirection.value;
  const active = segmentConfig(direction);

  // Everything the fetches depend on, in one comparable string (anchor covers the
  // dayBefore boundary the configs were built from).
  const queryKey = JSON.stringify([anchor, direction, active.live, active.hist, active.extra]);
  if (queryKey === lastCompletedKey && current !== null) {
    listRefetching.value = false; // the gesture ended on the same quantized inputs
    return;
  }

  const token = ++listToken;
  current?.controller.abort();
  const controller = new AbortController();

  const q: ActiveQuery = {
    token,
    order: anchor !== null && direction === "backward" ? "desc" : "asc",
    cursors: [],
    controller,
  };
  if (active.live) q.cursors.push(makeLiveCursor(active.live));
  if (active.hist) q.cursors.push(makeHistCursor(active.hist));
  if (active.extra) q.cursors.push(makeLiveCursor(active.extra, fetchExtraConcerts));
  current = q;

  listLoading.value = true;
  listRefetching.value = listItems.peek().length > 0;
  listError.value = null;

  try {
    // First pages of the active direction, THEN limit-1 probes of the other direction
    // (its total shows in the Upcoming/Past selector without fetching its records).
    // Sequenced, not parallel: D1 runs a database's queries serially, so four
    // simultaneous viewport misses (page + probe, live + archive) all landed together
    // at the sum of their costs — the list waited on the probes it doesn't need to
    // render. Now the reader's list is bounded by its own page fetches alone.
    await Promise.all(q.cursors.map((c) => fillCursor(q, c)));
    if (token !== listToken) return;

    // Publish the reader's list the moment its own pages are in. Only an EMPTY active
    // side has to wait for the probes (the auto-switch below needs the other side's
    // total to decide whether to flip direction).
    const activeTotal = q.cursors.reduce((a, c) => a + c.total, 0);
    const activeNotPerformed = q.cursors.reduce((a, c) => a + c.notPerformed, 0);
    let published = false;
    if (activeTotal > 0) {
      const items = await emitMerged(q, EMIT_CHUNK);
      if (token !== listToken) return;
      listItems.value = items;
      listHasMore.value = queryHasMore(q);
      listEpoch.value++;
      listRefetching.value = false;
      published = true;
    }

    // Each probe yields [rows matched, of which not performed].
    const probes: Promise<[number, number]>[] = [];
    if (anchor !== null) {
      const other = segmentConfig(direction === "forward" ? "backward" : "forward");
      if (other.live) {
        probes.push(
          fetchConcerts(other.live, { limit: 1, offset: 0, signal: controller.signal }).then(
            (p): [number, number] => [p.total, p.notPerformed],
          ),
        );
      }
      if (other.extra) {
        probes.push(
          fetchExtraConcerts(other.extra, { limit: 1, offset: 0, signal: controller.signal }).then(
            (p): [number, number] => [p.total, p.notPerformed],
          ),
        );
      }
      if (other.hist) {
        probes.push(
          fetchHistoricalConcerts(other.hist, {
            limit: 1,
            offset: 0,
            signal: controller.signal,
          }).then((p): [number, number] => [p.total, 0]),
        );
      }
    }
    const probeTotals = await Promise.all(probes);
    if (token !== listToken) return;

    const otherTotal = probeTotals.reduce((a, [n]) => a + n, 0);
    const otherNotPerformed = probeTotals.reduce((a, [, x]) => a + x, 0);
    // The displayed counts are performances — the same numbers the map's markers quote.
    const activeShown = activeTotal - activeNotPerformed;
    const otherShown = otherTotal - otherNotPerformed;
    if (anchor === null || direction === "forward") {
      forwardTotal.value = activeShown;
      backwardTotal.value = anchor === null ? 0 : otherShown;
    } else {
      backwardTotal.value = activeShown;
      forwardTotal.value = otherShown;
    }
    notPerformedTotal.value = activeNotPerformed;
    totalsLoaded.value = true;

    // Empty-side auto-switch: a filter whose results are all on the other side of the
    // anchor (a composer with only past concerts, a freshly-added org with nothing past)
    // must not land on an empty list with the real results hidden behind the unnoticed
    // other tab. Only while the reader hasn't picked a direction themselves; never a
    // history entry (it's a correction of the same view, not a navigation step). The
    // switched-to direction re-queries and, being non-empty, is stable — no ping-pong.
    if (anchor !== null && activeTotal === 0 && otherTotal > 0 && !listDirectionExplicit.peek()) {
      hintHistoryReplace();
      listDirection.value = direction === "forward" ? "backward" : "forward";
      return;
    }

    if (!published) {
      const items = await emitMerged(q, EMIT_CHUNK);
      if (token !== listToken) return;
      listItems.value = items;
      listHasMore.value = queryHasMore(q);
      listEpoch.value++;
    }
    lastCompletedKey = queryKey;
  } catch (err) {
    if (isAbort(err) || token !== listToken) return;
    lastCompletedKey = null;
    listError.value = err instanceof Error ? err.message : String(err);
    listItems.value = [];
    listHasMore.value = false;
    listEpoch.value++;
  } finally {
    if (token === listToken) {
      listLoading.value = false;
      listRefetching.value = false;
    }
  }
}

/** Fetch and append the next merged chunk (infinite scroll). */
export async function loadMoreItems(): Promise<void> {
  const q = current;
  if (!q || q.token !== listToken || listLoading.value || !listHasMore.value) return;
  listLoading.value = true;
  try {
    const more = await emitMerged(q, EMIT_CHUNK);
    if (q.token !== listToken) return;
    listItems.value = [...listItems.value, ...more];
    listHasMore.value = queryHasMore(q);
  } catch (err) {
    if (q.token === listToken && !isAbort(err)) console.error("load more failed", err);
  } finally {
    if (q.token === listToken) listLoading.value = false;
  }
}

// ── Signal wiring ──────────────────────────────────────────────────

let pieceToken = 0;

/** Resolve `selectedPieceKey` → `selectedPiece` when only the key is known (the cold
 *  shared-URL path: the key filters from first paint; this fetch names the chip). */
function startPieceResolver(): void {
  effect(() => {
    const key = selectedPieceKey.value;
    const have = selectedPiece.peek(); // peek: writing selectedPiece must not re-run this
    if (key === null || have?.key === key) return;

    const token = ++pieceToken;
    fetchPieceByKey(key)
      .then((piece) => {
        if (token === pieceToken && selectedPieceKey.peek() === key) {
          selectedPiece.value = piece;
        }
      })
      .catch((err) => {
        if (!isAbort(err)) console.error("piece lookup failed", err);
      });
  });
}

let countController: AbortController | null = null;
let histCountController: AbortController | null = null;

export function startQuerySync(): void {
  startPieceResolver();

  // Any filter change re-arms the empty-side auto-switch: the direction the reader picked
  // for one filter set isn't a standing order for the next. First run is registration,
  // not a change — it must not clobber an explicit ?dir= from the URL.
  let firstFilterRun = true;
  effect(() => {
    selectedComposerId.value;
    selectedPieceKey.value;
    selectedOrgId.value;
    selectedCityId.value;
    radiusKm.value;
    selectedCountry.value;
    selectedGenre.value;
    timeFrom.value;
    timeTo.value;
    liveEnabled.value;
    excludedArchiveKeys.value;
    certParam.value;
    showExtras.value;
    if (firstFilterRun) {
      firstFilterRun = false;
      return;
    }
    listDirectionExplicit.value = false;
  });

  // Live map marker counts. Light debounce coalesces batched filter writes. Counts cover
  // the WHOLE window (both directions) — the map is not direction-split, only the list is.
  let countTimer: ReturnType<typeof setTimeout> | null = null;
  effect(() => {
    if (!liveEnabled.value) {
      cityCounts.value = new Map();
      cityCountsLoaded.value = true;
      return;
    }
    const f: CountFilters = {
      composer: selectedComposerId.value,
      piece: selectedPieceKey.value,
      genre: selectedGenre.value || null,
      org: selectedOrgId.value,
      from: timeFrom.value || null,
      to: timeTo.value || null,
      // The list's Upcoming/Past split date — sent so each city's count comes back
      // split the same way and the popup can't disagree with the list.
      anchor: anchorISO.value,
    };
    const extras = showExtras.value && viewMode.value === "browse";
    if (countTimer) clearTimeout(countTimer);
    countTimer = setTimeout(() => {
      countController?.abort();
      countController = new AbortController();
      const { signal } = countController;
      // The opted-in rows land on the same markers: one dot per city, one count.
      const counts = extras
        ? Promise.all([fetchCityCounts(f, { signal }), fetchExtraCityCounts(f, { signal })]).then(([a, b]) => addCityCounts(a, b))
        : fetchCityCounts(f, { signal });
      counts
        .then((m) => {
          cityCounts.value = m;
          cityCountsLoaded.value = true;
        })
        .catch((err) => {
          if (!isAbort(err)) console.error("city-counts failed", err);
        });
    }, 120);
  });

  // Archive map marker counts — same shape. Skipped (and emptied) when the archive can't
  // answer the active filters (org/genre) or every archive source is excluded.
  let histCountTimer: ReturnType<typeof setTimeout> | null = null;
  effect(() => {
    const applicable =
      archiveEnabled.value &&
      selectedOrgId.value === null &&
      !selectedGenre.value;
    if (!applicable) {
      historicalCityCounts.value = new Map();
      historicalCityCountsLoaded.value = true;
      return;
    }
    const f = {
      from: timeFrom.value || null,
      to: timeTo.value || null,
      composer: selectedComposerId.value,
      piece: selectedPieceKey.value,
      xsrc: excludedArchiveKeys.value,
      cert: certParam.value,
    };
    if (histCountTimer) clearTimeout(histCountTimer);
    histCountTimer = setTimeout(() => {
      histCountController?.abort();
      histCountController = new AbortController();
      fetchHistoricalCityCounts(f, { signal: histCountController.signal })
        .then((m) => {
          historicalCityCounts.value = m;
          historicalCityCountsLoaded.value = true;
        })
        .catch((err) => {
          if (!isAbort(err)) console.error("historical city-counts failed", err);
        });
    }, 120);
  });

  // Mentions Reader availability probe: when a composer (or a resolved piece) is selected,
  // ask how many grounded memoir/letter mentions match (a limit-1 count query), to light up
  // the "Read N mentions" affordance. Independent of the map/list — mentions have no
  // time/place filter. With a piece selected the probe is WORK-scoped: the general
  // composer-wide count under a piece search read as "17 mentions of this piece" when the
  // 17 were mentions of anything by the composer. An unresolved piece (d… key) has no work
  // id to scope by, so it shows no affordance at all rather than a mislabeled one.
  let mentionProbeController: AbortController | null = null;
  effect(() => {
    const composer = selectedComposerId.value;
    const pieceKey = selectedPieceKey.value;
    const work = pieceWorkId(pieceKey);
    if ((composer == null && work == null) || (pieceKey != null && work == null)) {
      mentionCount.value = null;
      return;
    }
    mentionProbeController?.abort();
    mentionProbeController = new AbortController();
    const signal = mentionProbeController.signal;
    fetchHistoricalMentions({ composer, work }, { limit: 1, signal })
      .then((page) => {
        // Guard against a stale response for a since-changed filter.
        if (selectedComposerId.peek() === composer && selectedPieceKey.peek() === pieceKey) {
          mentionCount.value = page.total;
        }
      })
      .catch((err) => {
        if (!isAbort(err)) console.error("mention probe failed", err);
      });
  });

  // The unified list. Heavier debounce — bbox changes fire while panning the map.
  let listTimer: ReturnType<typeof setTimeout> | null = null;
  effect(() => {
    // Subscribe to everything a query reads (segmentConfig reads them inside the
    // debounce, after this effect has already registered the dependencies).
    selectedComposerId.value;
    selectedPieceKey.value;
    selectedOrgId.value;
    selectedCityId.value;
    radiusKm.value;
    radiusCenter.value; // re-run when a cold-loaded radius resolves its center
    selectedCountry.value;
    selectedGenre.value;
    timeFrom.value;
    timeTo.value;
    listDirection.value;
    liveEnabled.value;
    archiveEnabled.value;
    excludedArchiveKeys.value;
    certParam.value;
    showExtras.value;
    if (viewMode.value === "browse") mapBounds.value;
    if (listTimer) clearTimeout(listTimer);
    listTimer = setTimeout(() => {
      void runUnifiedQuery();
    }, 200);
  });
}

function isAbort(err: unknown): boolean {
  return err instanceof DOMException && err.name === "AbortError";
}
